import { WOODLAND_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/woodlandBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Twilight / Duskfall Vale — the dusk magic-hour: the in-between of day and night.
 *  A deep purple-blue zenith bleeds down into a warm orange-pink horizon, a big soft
 *  setting sun resting low on the skyline, first stars twinkling out (sparkle motes)
 *  while warm fireflies wake over the grass, the day's last warm light-shafts, a slow
 *  sunset bloom + cool zenith glow, a violet/amber light bed, dusk pollen drifting on a
 *  gentle breeze, fireflies clustering low, rolling-hill silhouettes deepening into the
 *  vale, and a cool-violet→warm-peach grade. Peaceful, nostalgic, transitional —
 *  contrast kept (a 'normal' grade veil, no pale wash). */
export const TWILIGHT: AtmospherePreset = {
  id: 'twilight',
  layers: [
    // Dusk sky — deep purple-blue zenith → warm orange-pink horizon (kept saturated so
    // the rolling-hill silhouettes below read as real depth against the sunset).
    { kind: 'skyGradient', from: 0x231d4e, to: 0xe5835e },
    // The setting sun resting low on the skyline — a big soft warm body + orange halo,
    // the light source the rays + sunset bloom read from.
    { kind: 'celestial', atX: 0.5, atY: 0.7, color: 0xffd49a, haloColor: 0xff9e63, size: 120, haloScale: 3.2, coreAlpha: 0.7, haloAlpha: 0.22 },
    // First stars emerging — tiny pale sparkle motes that barely drift, just twinkle
    // (sparkle texture, cool pale violet/blue, async alpha waver).
    { kind: 'fireflies', textureKey: 'sparkle', count: 30, colors: [P.nexusMote, 0xeaf2ff, P.ice], sizeMin: 2, sizeMax: 6, driftPx: 12, periodMinMs: 3000, periodMaxMs: 7000, alphaMin: 0.2, alphaMax: 0.9 },
    // A rare early meteor streaking the dusk — sparse + pale, the nostalgic "first
    // wishing star" of the magic hour.
    {
      kind: 'shootingStar', color: 0xfff0e6, count: 1,
      intervalMinMs: 9000, intervalMaxMs: 18000, lengthMin: 80, lengthMax: 150,
      thickness: 5, speed: 560, angleDeg: 20, alphaMax: 0.8,
    },
    // Fireflies waking over the grass at dusk — warm amber/pink, slow wander.
    { kind: 'fireflies', count: 22, colors: [P.firefly, P.softPink, P.amber], sizeMin: 5, sizeMax: 14, driftPx: 38, periodMinMs: 5000, periodMaxMs: 11000, alphaMin: 0.2, alphaMax: 0.75 },
    // The day's last warm light-shafts — soft + gentle, slowly breathing.
    { kind: 'godRays', count: 3, color: 0xffb27e, widthMin: 60, widthMax: 150, height: 540, tiltDeg: 8, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.08, alphaMax: 0.28 },
    // Sunset bloom low on the horizon + a cool violet zenith glow — the light swelling
    // slowly so the vale breathes (slow opacity breathe).
    breathingGlowBlobs(
      [
        { color: 0xffa566, atX: 0.5, atY: 0.72, size: 580, alpha: 0.13 }, // sunset bloom at the skyline
        { color: P.nexusViolet, atX: 0.5, atY: 0.08, size: 440, alpha: 0.07 }, // cool zenith dusk glow
      ],
      { periodMs: 6500, min: 0.85, max: 1.2 },
    ),
    // Soft violet + amber light bed shimmering low over the vale.
    { kind: 'lightField', count: 12, palette: [P.nexusMote, P.firefly, 0xff9e72], sizeMin: 120, sizeMax: 300, alphaMin: 0.04, alphaMax: 0.14, driftRadius: 60, periodMinMs: 7000, periodMaxMs: 13000 },
    // Dusk pollen drifting on a gentle evening breeze (warm cream → fading dusk violet),
    // density gusting on a slow offset cycle.
    gustingWeather(
      { mode: 'fall', color: 0xfbe6c6, colorEnd: 0xb49ad8,
        size: 6, frequencyMs: 120, lifespanMs: 5400, speed: 12, gravityY: 4, windX: 34, alphaEnd: 0.5 },
      { periodMs: 7500, min: 0.5, max: 1.4, phase: 0.3 },
    ),
    // Warm firefly clusters glimmering low over the grass.
    { kind: 'flicker', color: P.firefly, size: 270, baseAlpha: 0.05, flickerAmp: 0.05, hzMin: 2, hzMax: 6, anchors: [{ atX: 0.22, atY: 0.92 }, { atX: 0.52, atY: 0.95 }, { atX: 0.8, atY: 0.92 }] },
    // Rolling-hill silhouettes deepening into the vale (PX) read against the sunset sky —
    // far/mid rolling dunes + a near ridge, dusky violet. Far is tallest, a touch lighter
    // (atmospheric haze toward the warm horizon) + sways gently; near is shortest + darkest/most-opaque.
    ...multiPlaneParallax([
      { svg: WOODLAND_BACKDROPS.forest.far, shape: 'dunes', color: 0x4a3c72, speed: 0.04, height: 320, alpha: 0.7, detail: 0.7, sway: { ampPx: 8, periodMs: 32000 } },
      { svg: WOODLAND_BACKDROPS.forest.mid, shape: 'dunes', color: 0x332a58, speed: 0.10, height: 230, alpha: 0.84, detail: 1.0 },
      { svg: WOODLAND_BACKDROPS.forest.near, shape: 'ridge', color: 0x211b3c, speed: 0.18, height: 150, alpha: 0.94, detail: 1.1 },
    ]),
    ...biomeCloser({
      vignette: { color: 0x140f2e, edgeAlpha: 0.3, washColor: 0xffa066, washAlpha: 0.05 },
      // Twilight grade — cool dusk violet (top) → warm peach (bottom), a 'normal' veil that
      // tints the dusk gradient WITHOUT the 'screen' lift that would wash it pale (keeps contrast).
      grade: { top: 0x7d6ac0, bottom: 0xe89a6a, strength: 0.1, blend: 'normal' },
    }),
  ],
};
